import { useCallback, useEffect, useMemo, useState } from 'react';
import AnimateHeight, { Height } from 'react-animate-height';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { alertRulesActions } from '@/redux/actions';
import cx from 'classnames';
import {
  AlertRule,
  AlertRuleTxCountParams,
  AlertRuleType,
  AlertRuleCommonParams,
} from '@/api';
import { Switch, Tooltip } from 'antd';

import { AlertBadge, SignalBadge, SvgIcon } from '@/components';

import {
  BehavioralAlertsEntry,
  CategoriesValue,
} from '../BehavioralAlertsEntry/BehavioralAlertsEntry';
import styles from './BehavioralAlertsItem.module.sass';
import { DeleteRuleValue } from '../BehavioralAlertsDeleteRuleModal/BehavioralAlertsDeleteRuleModal.constants';
import {
  formatMinAmount,
  formatMaxAmount,
} from './BehavioralAlertsItem.helpers';
import { countriesList } from '@/components/CountrySelect';
import { getEmojiFlagByCode } from '@/components/CountrySelect/CountrySelect.helpers';

const tooltipStyle = {
  padding: '1rem',
  fontWeight: 500,
  color: 'black',
};

interface BehavioralAlertsItemProps {
  data: AlertRule;
  onDelete: (deletedRule: DeleteRuleValue) => void;
  onEdit: (id: AlertRule['id']) => void;
}

export const BehavioralAlertsItem = ({
  data,
  onDelete,
  onEdit,
}: BehavioralAlertsItemProps) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [childrenHeight, setChildrenHeight] = useState<Height>(0);
  const isShowNameTooltip = data.name.length > 20;
  const dispatch = useDispatch();

  const categories = useMemo(
    () => (data.categories?.length ? data.categories : []),
    [data.categories],
  );

  const isTxCount = data.type === AlertRuleType.TX_COUNT;
  const txParams = isTxCount ? (data.params as AlertRuleTxCountParams) : null;
  const commonParams = !isTxCount
    ? (data.params as AlertRuleCommonParams)
    : null;

  const directionValue = useMemo(() => {
    if (!data.direction || data.direction === 'all') {
      return t('translation.all');
    }
    return data.direction === 'income'
      ? t('translation.deposit')
      : t('translation.withdrawal');
  }, [data.direction, t]);

  const handleOnSwitchChange = useCallback(() => {
    if (!data.id) {
      return;
    }

    if (data.isActive) {
      dispatch(alertRulesActions.deactivateAlertRule({ id: data.id }));
    } else {
      dispatch(alertRulesActions.activateAlertRule({ id: data.id }));
    }
  }, [data.isActive, data.id, dispatch]);

  const handleOnDelete = useCallback(() => {
    onDelete({ id: data.id, name: data.name });
  }, [data.id, data.name, onDelete]);

  const handleOnEdit = useCallback(() => {
    onEdit(data.id);
  }, [data.id, onEdit]);

  useEffect(() => {
    setChildrenHeight(isExpanded ? 'auto' : 0);
  }, [isExpanded]);

  const classes = cx(styles.item, {
    [styles['is-active']]: data.isActive,
    [styles['is-expanded']]: isExpanded,
  });

  return (
    <div className={classes}>
      <div className={styles.row}>
        <div className={cx(styles.column, styles.controls)}>
          <button type="button" onClick={() => setIsExpanded(!isExpanded)}>
            <SvgIcon name="arrow-right-short" className={styles.arrow} />
          </button>

          <Switch
            checked={data.isActive}
            disabled={false}
            onChange={handleOnSwitchChange}
          />
        </div>

        <div className={cx(styles.column, styles.name)}>
          <Tooltip
            zIndex={500}
            arrow={false}
            color="white"
            styles={{ body: tooltipStyle }}
            title={isShowNameTooltip ? data.name : undefined}
          >
            <span>{data.name}</span>
          </Tooltip>
        </div>

        <div className={cx(styles.column, styles.amount)}>
          {isTxCount && txParams
            ? `${formatMinAmount(txParams.minAmount)} – ${formatMaxAmount(
                txParams.maxAmount,
              )}`
            : commonParams?.minVolume != null
            ? `≥${commonParams.minVolume}`
            : '—'}
        </div>

        <div className={cx(styles.column, styles.transfers)}>
          {isTxCount && txParams ? `≥${txParams.count}` : '—'}
        </div>

        <div className={cx(styles.column, styles.time)}>
          {isTxCount && txParams
            ? `${txParams.intervalValue} ${txParams.intervalType}${
                txParams.intervalValue > 1 ? 's' : ''
              }`
            : commonParams?.intervalValue != null && commonParams?.intervalType
            ? `${commonParams.intervalValue} ${commonParams.intervalType}`
            : '—'}
        </div>

        <div className={cx(styles.column, styles.category)}>
          {categories.length > 0 ? (
            <span className={styles.categoriesShort}>
              <SignalBadge value={categories[0]} />
              {categories.length > 1 && (
                <span className={styles.moreCount}>
                  +{categories.length - 1}
                </span>
              )}
            </span>
          ) : (
            '—'
          )}
        </div>

        <div className={cx(styles.column, styles.grade)}>
          <AlertBadge type={data.risk} />
        </div>

        <div className={cx(styles.column, styles.actions)}>
          <button
            type="button"
            className={styles.delete}
            onClick={handleOnDelete}
          >
            <SvgIcon name="delete-outline" />
          </button>
        </div>
      </div>

      <AnimateHeight duration={300} height={childrenHeight}>
        <div className={styles.details}>
          <div className={styles.groups}>
            <div className={styles.group}>
              <BehavioralAlertsEntry
                label={t('translation.rule_name')}
                value={data.name}
              />

              <BehavioralAlertsEntry
                label={t('translation.direction')}
                value={directionValue}
              />

              {isTxCount && txParams && (
                <BehavioralAlertsEntry
                  label={t('translation.amount_range')}
                  value={`${formatMinAmount(
                    txParams.minAmount,
                  )} – ${formatMaxAmount(txParams.maxAmount)}`}
                />
              )}

              {commonParams && commonParams.minVolume != null && (
                <BehavioralAlertsEntry
                  label={t('translation.min_amount')}
                  value={`≥${commonParams.minVolume}`}
                />
              )}

              <BehavioralAlertsEntry
                label={t('translation.alert_grade')}
                value={<AlertBadge isTextOnly type={data.risk} />}
              />
            </div>

            <div className={styles.group}>
              {isTxCount && txParams && (
                <>
                  <BehavioralAlertsEntry
                    label={t('translation.number_of_transfers')}
                    value={`at least ${txParams.count}`}
                  />

                  <BehavioralAlertsEntry
                    label={t('translation.time_period')}
                    value={`${txParams.intervalValue} ${txParams.intervalType}${
                      txParams.intervalValue > 1 ? 's' : ''
                    }`}
                  />
                </>
              )}

              {commonParams &&
                commonParams.intervalValue != null &&
                commonParams.intervalType && (
                  <BehavioralAlertsEntry
                    label={t('translation.time_period')}
                    value={`${commonParams.intervalValue} ${commonParams.intervalType}`}
                  />
                )}

              {commonParams &&
                (() => {
                  const params = commonParams as AlertRuleCommonParams & {
                    country?: string;
                  };
                  const codes = params.countries?.length
                    ? params.countries
                    : params.country
                    ? [params.country]
                    : [];
                  return codes.length > 0 ? (
                    <BehavioralAlertsEntry
                      label={t('translation.countries')}
                      value={codes
                        .map(
                          (code) =>
                            `${getEmojiFlagByCode(code)} ${
                              countriesList[
                                code as keyof typeof countriesList
                              ] ?? '—'
                            }`,
                        )
                        .join(', ')}
                    />
                  ) : null;
                })()}

              {commonParams && commonParams.isSuspended != null && (
                <BehavioralAlertsEntry
                  label={t('translation.suspended')}
                  value={
                    commonParams.isSuspended
                      ? t('translation.yes')
                      : t('translation.no')
                  }
                />
              )}

              {commonParams && commonParams.isBlacklisted != null && (
                <BehavioralAlertsEntry
                  label={t('translation.check_if_counterparty_blacklisted')}
                  value={
                    commonParams.isBlacklisted
                      ? t('translation.yes')
                      : t('translation.no')
                  }
                />
              )}

              {commonParams && commonParams.targetCustomerRisk != null && (
                <BehavioralAlertsEntry
                  label={t('translation.target_customer_risk')}
                  value={
                    <AlertBadge
                      isTextOnly
                      type={commonParams.targetCustomerRisk}
                    />
                  }
                />
              )}

              {commonParams &&
                commonParams.inactivityValue != null &&
                commonParams.inactivityType && (
                  <BehavioralAlertsEntry
                    label={t('translation.inactivity_interval')}
                    value={`${commonParams.inactivityValue} ${commonParams.inactivityType}`}
                  />
                )}

              <BehavioralAlertsEntry
                label={t('translation.categories')}
                value={<CategoriesValue categories={categories} />}
              />

              {(data.categoryJoin ?? data.categoryJoinCondition) != null && (
                <BehavioralAlertsEntry
                  label={t('translation.category_join_label')}
                  value={
                    (data.categoryJoin ?? data.categoryJoinCondition) === 'all'
                      ? t('translation.category_join_all')
                      : t('translation.category_join_any')
                  }
                />
              )}

              {/* <BehavioralAlertsEntry
                label={t('translation.description')}
                value="Description of the rule"
              /> */}
            </div>
          </div>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.delete}
              onClick={handleOnDelete}
            >
              <span>{t('translation.delete')}</span>
            </button>

            <button
              type="button"
              className={styles.edit}
              onClick={handleOnEdit}
            >
              <SvgIcon name="pencil-outline" />
              <span>{t('translation.edit')}</span>
            </button>
          </div>
        </div>
      </AnimateHeight>
    </div>
  );
};
